import type {
  Cache,
  Entity,
  FieldArgs,
  Link,
  Variables,
} from '@urql/exchange-graphcache';

type ListPartition = string | number | boolean | null;

export type CachedListBaseQueryCache = {
  inspectFields: Cache['inspectFields'];
  keyOfEntity: Cache['keyOfEntity'];
  resolve: Cache['resolve'];
  link(
    entity: Entity,
    field: string,
    args: FieldArgs,
    link: Link<Entity>,
  ): void;
};

export type CachedListBaseQueryConfig<
  TEntity,
  TPartition extends ListPartition,
> = {
  parentKey: string;
  fieldName: string;
  getEntityKey(cache: CachedListBaseQueryCache, entity: TEntity): string | null;
  getLinkPartition(
    cache: CachedListBaseQueryCache,
    entityKey: string,
  ): TPartition | undefined;
  getBaseQueryPartition(arguments_: Variables): TPartition | undefined;
};

export default function insertIntoCachedListBaseQueries<
  TEntity,
  TPartition extends ListPartition,
>(
  cache: CachedListBaseQueryCache,
  entities: Iterable<TEntity>,
  config: CachedListBaseQueryConfig<TEntity, TPartition>,
) {
  const linksByPartition = new Map<TPartition, string[]>();
  const seenEntityKeys = new Set<string>();

  for (const entity of entities) {
    const entityKey = config.getEntityKey(cache, entity);
    if (!entityKey || seenEntityKeys.has(entityKey)) {
      continue;
    }
    seenEntityKeys.add(entityKey);

    const partition = config.getLinkPartition(cache, entityKey);
    if (partition === undefined) {
      continue;
    }

    const partitionLinks = linksByPartition.get(partition) ?? [];
    partitionLinks.push(entityKey);
    linksByPartition.set(partition, partitionLinks);
  }

  if (linksByPartition.size === 0) {
    return;
  }

  for (const field of cache.inspectFields(config.parentKey)) {
    if (field.fieldName !== config.fieldName || field.arguments === null) {
      continue;
    }

    const partition = config.getBaseQueryPartition(field.arguments);
    if (partition === undefined) {
      continue;
    }

    const partitionLinks = linksByPartition.get(partition);
    if (!partitionLinks) {
      continue;
    }

    const resolvedLinks = cache.resolve(config.parentKey, field.fieldKey);
    if (!Array.isArray(resolvedLinks)) {
      continue;
    }

    const existingLinkList = resolvedLinks.filter(
      (link): link is string => typeof link === 'string',
    );
    if (existingLinkList.length !== resolvedLinks.length) {
      continue;
    }

    const existingLinks = new Set(existingLinkList);
    const additions = partitionLinks.filter(
      (entityKey) => !existingLinks.has(entityKey),
    );
    if (additions.length === 0) {
      continue;
    }

    cache.link(
      config.parentKey,
      field.fieldName,
      field.arguments,
      additions.concat(existingLinkList),
    );
  }
}
